import { diffArrays } from 'diff';

// 改行コードを LF に正規化する（CRLF・CR → LF）。null/undefined は空文字
export const normalizeNewlines = (s) => String(s ?? '').replace(/\r\n?/g, '\n');

// ガターの色帯を出すときの編集距離（追加＋削除の行数）の上限。超えたら色帯を出さない
export const MARKS_MAX_EDIT_LINES = 2000;

/**
 * 2テキストの行単位差分を [{type:'eq'|'add'|'del', a?, b?}] で返す
 * 上限（maxEditLength）を超えたら null。壁時計の timeout は結果が揺れるため使わない
 */
export const computeDiffOps = (a, b, opts = undefined) => {
    const aLines = normalizeNewlines(a).split('\n');
    const bLines = normalizeNewlines(b).split('\n');
    const parts = diffArrays(aLines, bLines, opts);
    if (!parts) return null;
    return parts.flatMap((part) => part.value.map((line) => {
        if (part.added) return { type: 'add', b: line };
        if (part.removed) return { type: 'del', a: line };
        return { type: 'eq', a: line, b: line };
    }));
};

/**
 * 右テキストの各行に、左テキストとの違い（added / changed / removed / null）を付ける
 * 削除は右側に行が無いので、消えた位置の直後の行（末尾なら最終行）に印を付ける
 */
export const diffLineMarks = (leftText, rightText) => {
    const left = normalizeNewlines(leftText);
    const right = normalizeNewlines(rightText);
    const marks = new Array(right.split('\n').length).fill(null);
    if (left === right) return marks;
    // 行数の差だけで上限を超えるなら、差分を計算するまでもなく色帯を出さない
    if (Math.abs(left.split('\n').length - marks.length) > MARKS_MAX_EDIT_LINES) return marks;
    const ops = computeDiffOps(left, right, { maxEditLength: MARKS_MAX_EDIT_LINES });
    if (!ops) return marks;

    let i = 0;
    let pendingDel = 0;
    const markRemoved = () => {
        if (i < marks.length) marks[i] = marks[i] || 'removed';
        else if (marks.length) marks[marks.length - 1] = 'removed';
    };
    ops.forEach((op) => {
        if (op.type === 'eq') {
            if (pendingDel) markRemoved();
            pendingDel = 0;
            i += 1;
        } else if (op.type === 'del') {
            pendingDel += 1;
        } else {
            if (i < marks.length) marks[i] = pendingDel ? 'changed' : 'added';
            if (pendingDel) pendingDel -= 1;
            i += 1;
        }
    });
    if (pendingDel) markRemoved();
    return marks;
};

/**
 * 差分の前後 context 行だけ残し、離れた eq 行は省略マーカーにまとめる（1行だけの省略はそのまま見せる）
 * type が 'eq' 以外を差分とみなすため、pairDiffRows の行にも使える
 * @returns {Array<{type:'op', op, index}|{type:'skip', count, start}>}
 */
export const collapseDiffOps = (ops, context) => {
    const list = Array.isArray(ops) ? ops : [];
    if (!list.length) return [];
    const ctx = Math.max(0, Math.trunc(Number(context)) || 0);
    const keep = new Array(list.length).fill(false);
    list.forEach((op, i) => {
        if (op.type === 'eq') return;
        for (let k = Math.max(0, i - ctx); k <= Math.min(list.length - 1, i + ctx); k++) keep[k] = true;
    });

    const out = [];
    let hidden = 0;
    let hiddenStart = 0;
    const flush = () => {
        if (hidden === 1) out.push({ type: 'op', op: list[hiddenStart], index: hiddenStart });
        else if (hidden > 1) out.push({ type: 'skip', count: hidden, start: hiddenStart });
        hidden = 0;
    };
    list.forEach((op, i) => {
        if (keep[i]) {
            flush();
            out.push({ type: 'op', op, index: i });
        } else {
            if (!hidden) hiddenStart = i;
            hidden += 1;
        }
    });
    flush();
    return out;
};

// 書き換え行の文字差分で計算を打ち切る編集距離（文字数）
const CHAR_MAX_EDIT_LENGTH = 1000;
// 文字差分を取る行の長さの上限（左右の合計文字数）。超えたら行全体を塗る
const CHAR_MAX_LINE_LENGTH = 4000;

// 隣り合う同じ塗りのかけらをまとめる
const pushSegment = (segs, t, hl) => {
    if (!t) return;
    const last = segs[segs.length - 1];
    if (last && last.hl === hl) last.t += t;
    else segs.push({ t, hl });
};

/**
 * 書き換え前後の1行どうしの文字差分。変わった文字に hl:true を付ける
 * 1文字だけ一致して前後が変更なら変更側に寄せ、細切れを防ぐ（その側に変更が隣接するときだけ）。上限を超えたら行全体を塗る
 */
export const charDiffSegments = (a, b) => {
    const left = [];
    const right = [];
    const tooLong = (a ?? '').length + (b ?? '').length > CHAR_MAX_LINE_LENGTH;
    const parts = tooLong ? null : diffArrays(Array.from(a ?? ''), Array.from(b ?? ''), { maxEditLength: CHAR_MAX_EDIT_LENGTH });
    if (!parts) {
        pushSegment(left, a, true);
        pushSegment(right, b, true);
        return { left, right };
    }
    parts.forEach((part, k) => {
        const t = part.value.join('');
        const isEq = !part.added && !part.removed;
        const absorbable = isEq && part.value.length === 1 && k > 0 && k < parts.length - 1;
        const nextTo = (key) => absorbable && !!(parts[k - 1][key] || parts[k + 1][key]);
        if (!part.added) pushSegment(left, t, !isEq || nextTo('removed'));
        if (!part.removed) pushSegment(right, t, !isEq || nextTo('added'));
    });
    return { left, right };
};

/**
 * 行差分を表示行（eq / chg / del / add）に組み直す
 * 連続する削除と追加は i 番目どうしを書き換え（chg）として同じ行に並べ、余りは del / add のまま残す
 * 連続する変更行には同じかたまり番号（hunk）を振る
 * @returns {{rows: Array<{type, left, right, lnL, lnR, hunk?, segs?}>, hunks: number}}
 */
export const pairDiffRows = (ops) => {
    const list = Array.isArray(ops) ? ops : [];
    const rows = [];
    let a = 0;
    let b = 0;
    let hunks = 0;
    let k = 0;
    while (k < list.length) {
        if (list[k].type === 'eq') {
            rows.push({ type: 'eq', left: list[k].a, right: list[k].b, lnL: ++a, lnR: ++b });
            k += 1;
            continue;
        }
        const dels = [];
        const adds = [];
        for (; k < list.length && list[k].type !== 'eq'; k++) {
            if (list[k].type === 'del') dels.push(list[k].a);
            else adds.push(list[k].b);
        }
        const hunk = hunks++;
        for (let i = 0; i < Math.max(dels.length, adds.length); i++) {
            const hasL = i < dels.length;
            const hasR = i < adds.length;
            const row = { left: hasL ? dels[i] : '', right: hasR ? adds[i] : '', lnL: hasL ? a + i + 1 : null, lnR: hasR ? b + i + 1 : null, hunk };
            if (hasL && hasR) rows.push({ ...row, type: 'chg', segs: charDiffSegments(row.left, row.right) });
            else rows.push({ ...row, type: hasL ? 'del' : 'add' });
        }
        a += dels.length;
        b += adds.length;
    }
    return { rows, hunks };
};

// 追加・削除・書き換えの行数
export const diffRowStats = (rows) => {
    const stat = { add: 0, del: 0, chg: 0 };
    (Array.isArray(rows) ? rows : []).forEach((row) => {
        if (row.type in stat) stat[row.type] += 1;
    });
    return stat;
};
